import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CurrencyEur, ArrowSquareOut, ArrowCounterClockwise } from "@phosphor-icons/react";

function fmtEur(cents, ccy = "eur") {
  if (cents == null) return "—";
  const val = cents / 100;
  return val.toLocaleString("fr-FR", { style: "currency", currency: (ccy || "eur").toUpperCase(), maximumFractionDigits: 2 });
}

function fmtDate(unix) {
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleDateString("fr-FR");
}

export default function SuperadminStripe() {
  const [tab, setTab] = useState("subs");
  return (
    <SuperadminLayout title="Stripe" actions={
      <div className="flex gap-2">
        <Button size="sm" onClick={() => setTab("subs")} className={tab === "subs" ? "bg-[#E4572E] hover:bg-[#F27551]" : "bg-white/10 hover:bg-white/20 text-white"} data-testid="stripe-tab-subs">Abonnements</Button>
        <Button size="sm" onClick={() => setTab("invs")} className={tab === "invs" ? "bg-[#E4572E] hover:bg-[#F27551]" : "bg-white/10 hover:bg-white/20 text-white"} data-testid="stripe-tab-invs">Factures</Button>
      </div>
    }>
      {tab === "subs" ? <Subscriptions /> : <Invoices />}
    </SuperadminLayout>
  );
}

function Subscriptions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (status !== "all") params.status = status;
        const { data } = await api.get("/superadmin/stripe/subscriptions", { params });
        setItems(data.items);
      } catch (e) {
        toast.error("Chargement Stripe impossible");
      } finally { setLoading(false); }
    })();
  }, [status]);

  return (
    <>
      <div className="mb-4 max-w-xs">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="stripe-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="trialing">En essai</SelectItem>
            <SelectItem value="past_due">Paiement échoué</SelectItem>
            <SelectItem value="canceled">Résiliés</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DarkCard className="overflow-hidden" data-testid="subs-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#8494A8]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Client</th>
              <th className="text-left px-4 py-3 font-medium">Plan</th>
              <th className="text-left px-4 py-3 font-medium">Montant</th>
              <th className="text-left px-4 py-3 font-medium">Statut</th>
              <th className="text-left px-4 py-3 font-medium">Fin période</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-[#8494A8]">Chargement Stripe…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#8494A8]">Aucun abonnement</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <p className="text-white">{s.customer_name || "—"}</p>
                  <p className="text-[#8494A8] text-xs break-all">{s.customer_email || s.customer_id}</p>
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">
                  <p>{s.lookup_key || "—"}</p>
                  {s.quantity > 1 && <p className="text-[#8494A8]">×{s.quantity} sièges</p>}
                </td>
                <td className="px-4 py-3 text-white font-mono">{fmtEur((s.amount || 0) * (s.quantity || 1), s.currency)}<span className="text-[#8494A8] text-xs">/{s.interval || "?"}</span></td>
                <td className="px-4 py-3"><Badge className="bg-white/10 text-white/80 hover:bg-white/10">{s.status}</Badge>
                  {s.cancel_at_period_end && <Badge className="ml-1 bg-amber-500/20 text-amber-300 hover:bg-amber-500/20">Résiliation prévue</Badge>}
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">{fmtDate(s.current_period_end)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DarkCard>
    </>
  );
}

function Invoices() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [refundInv, setRefundInv] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      const { data } = await api.get("/superadmin/stripe/invoices", { params });
      setItems(data.items);
    } catch (e) { toast.error("Chargement Stripe impossible"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const refund = async (amount_cents, reason) => {
    try {
      const { data } = await api.post("/superadmin/stripe/refund", { invoice_id: refundInv.id, amount_cents: amount_cents || null, reason });
      toast.success(`Remboursement créé — ${fmtEur(data.amount)}`);
      setRefundInv(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Remboursement impossible"); }
  };

  return (
    <>
      <div className="mb-4 max-w-xs">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="paid">Payées</SelectItem>
            <SelectItem value="open">Ouvertes</SelectItem>
            <SelectItem value="uncollectible">Irrécouvrables</SelectItem>
            <SelectItem value="void">Annulées</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DarkCard className="overflow-hidden" data-testid="invs-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#8494A8]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Facture</th>
              <th className="text-left px-4 py-3 font-medium">Client</th>
              <th className="text-left px-4 py-3 font-medium">Montant</th>
              <th className="text-left px-4 py-3 font-medium">Statut</th>
              <th className="text-left px-4 py-3 font-medium">Créée</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-[#8494A8]">Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#8494A8]">Aucune facture</td></tr>}
            {items.map((inv) => (
              <tr key={inv.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-white font-mono text-xs">{inv.number || inv.id}</td>
                <td className="px-4 py-3">
                  <p className="text-white text-sm">{inv.customer_name || "—"}</p>
                  <p className="text-[#8494A8] text-xs break-all">{inv.customer_email}</p>
                </td>
                <td className="px-4 py-3 text-white font-mono">{fmtEur(inv.amount_paid || inv.amount_due, inv.currency)}</td>
                <td className="px-4 py-3">
                  <Badge className={inv.status === "paid" ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20" :
                                     inv.status === "open" ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/20" :
                                     "bg-white/10 text-white/70 hover:bg-white/10"}>{inv.status}</Badge>
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">{fmtDate(inv.created)}</td>
                <td className="px-4 py-3 text-right">
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-[#B7C4D6] hover:text-white text-xs inline-flex items-center gap-1 mr-2">
                      Voir <ArrowSquareOut size={12} />
                    </a>
                  )}
                  {inv.status === "paid" && (
                    <Button size="sm" variant="ghost" className="text-[#B7C4D6] hover:bg-white/10 hover:text-white" onClick={() => setRefundInv(inv)} data-testid={`refund-btn-${inv.id}`}>
                      <ArrowCounterClockwise size={14} className="mr-1" /> Rembourser
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DarkCard>
      {refundInv && <RefundDialog inv={refundInv} onConfirm={refund} onClose={() => setRefundInv(null)} />}
    </>
  );
}

function RefundDialog({ inv, onConfirm, onClose }) {
  const [full, setFull] = useState(true);
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white" data-testid="refund-dialog">
        <DialogHeader><DialogTitle>Rembourser {inv.number || inv.id}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-[#B7C4D6]">Montant facturé : <strong>{fmtEur(inv.amount_paid, inv.currency)}</strong></p>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)} className="w-4 h-4 accent-[#E4572E]" />
            <Label className="text-sm text-[#B7C4D6]">Remboursement complet</Label>
          </div>
          {!full && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Montant (centimes)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                className="mt-2 bg-[#081221] border-white/10 text-white" placeholder="ex: 2900" data-testid="refund-amount" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif obligatoire</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Demande utilisateur — ticket #123"
              className="mt-2 bg-[#081221] border-white/10 text-white" data-testid="refund-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button disabled={reason.trim().length < 3} onClick={() => onConfirm(full ? null : amount, reason.trim())}
            className="bg-[#E4572E] hover:bg-[#F27551] text-white" data-testid="refund-confirm">Confirmer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
